import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trophy, Zap, Info, Upload, CheckCircle2 } from "lucide-react";
import { api, brl, catName, imgSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Countdown } from "../components/Countdown";
import { PhotoCard } from "../components/PhotoCard";
import { Lightbox } from "../components/Lightbox";
import { CheckoutDialog } from "../components/CheckoutDialog";
import { UploadDialog } from "../components/UploadDialog";

const Podium = ({ photos, onOpen }) => (
  <div className="grid grid-cols-3 items-end gap-3" data-testid="contest-podium">
    {[1, 0, 2].map((idx) => {
      const p = photos[idx];
      if (!p) return <div key={idx} />;
      const h = idx === 0 ? "h-64" : idx === 1 ? "h-52" : "h-44";
      return (
        <button key={p.id} onClick={() => onOpen(p)} data-testid={`podium-${idx + 1}`} className="group text-left">
          <div className={`relative ${h} overflow-hidden rounded-xl border ${idx === 0 ? "border-lime-400" : "border-cyan-400/40"}`}>
            <img src={imgSrc(p.src)} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <span className={`absolute left-2 top-2 rounded px-2 py-0.5 font-display text-xl font-black ${idx === 0 ? "bg-lime-400 text-black" : "bg-cyan-400 text-black"}`}>#{idx + 1}</span>
          </div>
          <p className="mt-2 truncate font-display text-lg font-bold uppercase">{p.user_name}</p>
          <p className="font-mono-tech text-xs text-red-300">{p.likes} votos</p>
        </button>
      );
    })}
  </div>
);

export default function ContestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [upload, setUpload] = useState(false);

  const load = useCallback(() => api.get(`/contests/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (!c) return <div className="mx-auto h-96 max-w-7xl animate-pulse px-5 py-16" />;
  const needsFee = c.entry_fee > 0 && !c.paid;

  const participate = () => {
    if (!user) return nav("/cadastro");
    if (needsFee) return setCheckout(true);
    setUpload(true);
  };

  return (
    <div data-testid="contest-detail-page">
      <section className="noise relative overflow-hidden border-b border-white/10">
        <img src={c.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="grid-bg absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/70 to-transparent" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <div className="flex gap-2">
              {c.is_main && <span className="flex items-center gap-1 rounded bg-lime-400 px-2.5 py-1 font-mono-tech text-[10px] font-bold uppercase text-black"><Zap className="h-3 w-3" /> Concurso principal</span>}
              <span className="rounded bg-black/60 px-2.5 py-1 font-mono-tech text-[10px] uppercase text-cyan-300">{catName(c.category)}</span>
            </div>
            <h1 className="mt-4 font-display text-5xl font-black uppercase leading-none sm:text-6xl" data-testid="contest-title">{c.title}</h1>
            <p className="mt-4 max-w-xl text-slate-300">{c.description}</p>
            <div className="mt-6 flex items-center gap-2 text-lg"><Trophy className="h-5 w-5 text-lime-300" /> <b data-testid="contest-prize">{c.prize}</b></div>
          </div>
          <div className="glass rounded-2xl p-6 lg:col-span-5" data-testid="contest-entry-panel">
            <p className="font-mono-tech text-xs uppercase tracking-widest text-slate-400">{c.status === "active" ? "Encerra em" : "Status"}</p>
            <div className="mt-3"><Countdown deadline={c.deadline} /></div>
            <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
              <span className="text-slate-400">Taxa de inscrição</span>
              <span className="font-display text-3xl font-black" data-testid="contest-entry-fee">{c.entry_fee > 0 ? brl(c.entry_fee) : "Grátis"}</span>
            </div>
            {c.paid && <p className="mt-2 flex items-center gap-1.5 text-sm text-lime-300" data-testid="contest-paid-badge"><CheckCircle2 className="h-4 w-4" /> Inscrição paga</p>}
            {c.is_main && <p className="mt-3 flex gap-2 text-xs text-slate-400"><Info className="h-4 w-4 shrink-0 text-cyan-300" /> A foto gratuita do cadastro não participa de concursos principais — use uma foto extra.</p>}
            {c.status === "active" && (
              <button data-testid="contest-participate-btn" onClick={participate} className="btn-neon mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-semibold">
                <Upload className="h-4 w-4" /> {needsFee && user ? `Pagar inscrição e participar` : "Participar com minha foto"}
              </button>
            )}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        {c.photos.length >= 3 && (
          <div className="mb-16">
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">// Ranking ao vivo</p>
            <h2 className="mb-8 mt-2 font-display text-4xl font-black uppercase">Pódio</h2>
            <Podium photos={c.photos} onOpen={setOpen} />
          </div>
        )}
        <h2 className="mb-8 font-display text-4xl font-black uppercase">Fotos participantes <span className="text-cyan-400">({c.photos.length})</span></h2>
        {c.photos.length === 0 && <p className="text-slate-400" data-testid="contest-no-photos">Ainda não há fotos aprovadas. Seja o primeiro!</p>}
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
          {c.photos.map((p, i) => <PhotoCard key={p.id} photo={p} rank={i + 1} index={i} onOpen={setOpen} />)}
        </div>
      </section>
      <Lightbox photo={open} onClose={() => setOpen(null)} />
      <CheckoutDialog open={checkout} onOpenChange={setCheckout} contest={needsFee ? c : null} />
      <UploadDialog open={upload} onOpenChange={setUpload} presetContest={c} onUploaded={load} onNeedCredits={() => setCheckout(true)} />
    </div>
  );
}
